/**
 * Vekalet Ücreti (Avukatlık Asgari Ücret Tarifesi) hesap motoru
 * ---------------------------------------------------------------
 * Kaynak: 2025-2026 Avukatlık Asgari Ücret Tarifesi
 *         04.11.2025 tarihli, 33067 sayılı Resmî Gazete
 *         (1136 s. Avukatlık Kanunu m.164 uyarınca TBB tarifesi)
 *
 * DOĞRULAMA NOTU (2026-09-03):
 * Dilim tablosu iki bağımsız kaynaktan teyit edildi ve iç tutarlılık
 * testi geçti: dilimlerin toplamı tam 18.600.000 TL ediyor; bu da
 * tarifedeki "18.600.000 TL'den yukarısı" eşiğiyle birebir örtüşüyor.
 * 2026 tarifesinde oranlar aynı kalmış, dilim tutarları yükselmiş ve
 * araya yeni bir %13'lük dilim eklenmiştir (2025'te 9, 2026'da 10 dilim).
 *
 * Yeni tarife çıktığında yalnızca AAUT_NISPI_DILIMLER güncellenir.
 */

export const AAUT_TARIFE_ADI = '2025-2026 AAÜT';
export const AAUT_RESMI_GAZETE = '04.11.2025 · 33067 sayılı Resmî Gazete';

/** Üçüncü Kısım — nispi (oransal) tarife. Sıra önemlidir. */
export const AAUT_NISPI_DILIMLER = [
  { tutar: 600000, oran: 16, etiket: 'İlk 600.000 TL' },
  { tutar: 600000, oran: 15, etiket: 'Sonraki 600.000 TL' },
  { tutar: 1200000, oran: 14, etiket: 'Sonraki 1.200.000 TL' },
  { tutar: 1200000, oran: 13, etiket: 'Sonraki 1.200.000 TL' },
  { tutar: 1800000, oran: 11, etiket: 'Sonraki 1.800.000 TL' },
  { tutar: 2400000, oran: 8, etiket: 'Sonraki 2.400.000 TL' },
  { tutar: 3000000, oran: 5, etiket: 'Sonraki 3.000.000 TL' },
  { tutar: 3600000, oran: 3, etiket: 'Sonraki 3.600.000 TL' },
  { tutar: 4200000, oran: 2, etiket: 'Sonraki 4.200.000 TL' },
  { tutar: Infinity, oran: 1, etiket: '18.600.000 TL üzeri' },
];

/** Dilimlerin toplamı — tablo doğruluğunun iç kontrolü. */
export const AAUT_UST_ESIK = AAUT_NISPI_DILIMLER
  .filter((d) => Number.isFinite(d.tutar))
  .reduce((t, d) => t + d.tutar, 0); // = 18.600.000

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Nispi vekalet ücretini kademeli olarak hesaplar.
 * @param {number} deger Dava değeri / kabul veya reddedilen miktar (TL)
 * @returns {{ucret:number, kirilim:Array, deger:number}}
 */
export function hesaplaNispiUcret(deger) {
  const tutar = Math.max(0, Number(deger) || 0);
  let kalan = tutar;
  let ucret = 0;
  const kirilim = [];

  for (const dilim of AAUT_NISPI_DILIMLER) {
    if (kalan <= 0) break;
    const uygulanan = Number.isFinite(dilim.tutar) ? Math.min(kalan, dilim.tutar) : kalan;
    const parca = uygulanan * (dilim.oran / 100);
    ucret += parca;
    kirilim.push({
      etiket: dilim.etiket,
      oran: dilim.oran,
      matrah: round2(uygulanan),
      ucret: round2(parca),
    });
    kalan -= uygulanan;
  }

  return { ucret: round2(ucret), kirilim, deger: tutar };
}

/**
 * AAÜT m.13 kurallarını uygulayarak nihai ücreti belirler:
 *
 *  • Nispi ücret, davanın görüldüğü mahkeme için tarifede gösterilen
 *    MAKTU ücretin altında olamaz (m.13/1).
 *  • Hükmedilen vekalet ücreti, kabul veya reddedilen miktarı GEÇEMEZ
 *    (m.13/2).
 *
 * @param {number} deger   Kabul/reddedilen miktar (TL)
 * @param {number} maktu   İlgili mahkeme için maktu ücret (TL) — 0 ise uygulanmaz
 */
export function hesaplaVekaletUcreti(deger, maktu = 0) {
  const nispi = hesaplaNispiUcret(deger);
  const maktuTutar = Math.max(0, Number(maktu) || 0);

  const notlar = [];
  let sonuc = nispi.ucret;

  if (maktuTutar > 0 && sonuc < maktuTutar) {
    notlar.push({
      tur: 'maktu',
      metin: `Nispi ücret (${round2(nispi.ucret)} TL) maktu ücretin altında kaldığı için ` +
             `AAÜT m.13/1 uyarınca maktu ücret uygulandı.`,
    });
    sonuc = maktuTutar;
  }

  // m.13/2: ücret, kabul veya reddedilen miktarı geçemez
  if (nispi.deger > 0 && sonuc > nispi.deger) {
    notlar.push({
      tur: 'tavan',
      metin: `Hesaplanan ücret, kabul/reddedilen miktarı (${round2(nispi.deger)} TL) ` +
             `aştığı için AAÜT m.13/2 uyarınca bu miktara indirildi.`,
    });
    sonuc = nispi.deger;
  }

  return {
    ...nispi,
    maktu: maktuTutar,
    nispiUcret: nispi.ucret,
    sonuc: round2(sonuc),
    notlar,
  };
}

export const formatTL = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * "1.234,56" / "1.250.000" / "1234.56" gibi girdileri sayıya çevirir.
 *
 * DİKKAT: Yalnızca nokta içeren girdi belirsizdir — "1.250.000" Türkçe
 * yazımda binlik ayracıdır, "1.25" ise ondalıktır. Nokta grupları tam
 * 3 haneliyse binlik ayracı kabul edilir. (Bu ayrım yapılmazsa
 * parseFloat("1.250.000") = 1,25 döner ve tutar 1.000.000 kat küçülür.)
 */
export function parseTutar(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  let s = String(value).trim().replace(/\s/g, '');
  if (!s) return 0;

  const negatif = s.startsWith('-');
  if (negatif) s = s.slice(1);

  if (s.includes(',')) {
    // Virgül varsa o ondalıktır; noktalar binlik ayracıdır.
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(s)) {
    // Sadece nokta var ve tüm gruplar 3 haneli -> binlik ayracı
    s = s.replace(/\./g, '');
  }
  // Aksi halde nokta ondalık kabul edilir ("1.25")

  const n = parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return negatif ? -n : n;
}

/* ===========================================================================
   MAHKEMELERE GÖRE MAKTU ÜCRETLER — İkinci Kısım İkinci Bölüm
   ---------------------------------------------------------------------------
   Kaynak: 2025-2026 AAÜT. Değerler Türkiye Barolar Birliği'nin resmî
   hesaplama aracından teyit edildi (17.09.2026): her mahkeme için
   100.000 TL'lik dava sorgulandı; nispi ücret (16.000 TL) maktunun
   altında kaldığı için dönen sonuç doğrudan maktu ücrettir.

   29 senaryoda (icra + 7 mahkeme × 3 tutar) sonuçlar TBB aracıyla
   birebir uyuştu.
   =========================================================================== */
export const AAUT_MAHKEME_MAKTU = [
  { anahtar: 'asliye',          ad: 'Asliye Mahkemeleri',              maktu: 45000 },
  { anahtar: 'sulhHukuk',       ad: 'Sulh Hukuk Mahkemeleri',          maktu: 30000 },
  { anahtar: 'sulhCeza',        ad: 'Sulh Ceza Hâkimlikleri',          maktu: 18000 },
  { anahtar: 'tuketici',        ad: 'Tüketici Mahkemeleri',            maktu: 22500 },
  { anahtar: 'fikri',           ad: 'Fikrî ve Sınaî Haklar Mahkemeleri', maktu: 55000 },
  { anahtar: 'idareDurusmali',  ad: 'İdare/Vergi — duruşmalı',         maktu: 40000 },
  { anahtar: 'idareDurusmasiz', ad: 'İdare/Vergi — duruşmasız',        maktu: 30000 },
];

/** İcra dairelerinde yapılan takipler için maktu ücret. */
export const AAUT_ICRA_MAKTU = 9000;

/**
 * AAÜT m.11/1 — takip miktarı bu tutara kadar olan icra takiplerinde
 * maktu ücret uygulanır; üzerinde üçüncü kısma göre nispi hesaplanır.
 */
export const AAUT_ICRA_NISPI_ESIK = 56250;

/**
 * İcra takibi vekalet ücreti (AAÜT m.11).
 *
 * Tarifenin sözü:
 *   "...takip miktarı 56.250,00 TL'ye kadar olan icra takiplerinde
 *    avukatlık ücreti, bu Tarifenin ikinci kısmının ikinci bölümünde,
 *    icra dairelerindeki takipler için öngörülen maktu ücrettir.
 *    ANCAK, BU ÜCRET ASIL ALACAĞI GEÇEMEZ."
 *
 * m.11/4: borçlu ödeme süresi içinde öderse ücretin dörtte üçü.
 *
 * @param {number} takipMiktari Takip talebindeki tutar (TL)
 * @param {boolean} sureIcindeOdendi Borçlu ödeme süresinde ödedi mi
 */
export function hesaplaIcraVekaletUcreti(takipMiktari, sureIcindeOdendi = false) {
  const tutar = Math.max(0, Number(takipMiktari) || 0);
  const nispi = hesaplaNispiUcret(tutar);
  const notlar = [];

  let sonuc;
  let maktuUygulandi = false;

  if (tutar <= AAUT_ICRA_NISPI_ESIK) {
    sonuc = AAUT_ICRA_MAKTU;
    maktuUygulandi = true;
    notlar.push({
      tur: 'maktu',
      metin: `Takip miktarı ${formatTL(AAUT_ICRA_NISPI_ESIK)} TL'ye kadar olduğu için `
           + `AAÜT m.11/1 uyarınca maktu ücret uygulandı.`,
    });

    if (tutar > 0 && sonuc > tutar) {
      sonuc = tutar;
      notlar.push({
        tur: 'tavan',
        metin: 'Maktu ücret asıl alacağı geçemeyeceği için (AAÜT m.11/1 son cümle) '
             + 'ücret asıl alacak kadar belirlendi.',
      });
    }
  } else {
    sonuc = nispi.ucret;
  }

  const ceyrekOncesi = sonuc;
  if (sureIcindeOdendi) {
    sonuc = round2(sonuc * 0.75);
    notlar.push({
      tur: 'ceyrek',
      metin: 'Borçlu ödeme süresi içinde ödediği için AAÜT m.11/4 uyarınca '
           + 'ücretin dörtte üçü uygulandı.',
    });
  }

  return {
    ...nispi,
    maktu: maktuUygulandi ? AAUT_ICRA_MAKTU : 0,
    nispiUcret: nispi.ucret,
    ceyrekOncesi: round2(ceyrekOncesi),
    sonuc: round2(sonuc),
    notlar,
  };
}
